
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Task } from '@/types/tasks';

interface UseTaskDragOptions {
  id: string;
  task: Task;
  containerId?: string;
}

interface UseTaskDragResult {
  attributes: ReturnType<typeof useSortable>['attributes'];
  listeners: ReturnType<typeof useSortable>['listeners'];
  setNodeRef: ReturnType<typeof useSortable>['setNodeRef'];
  setActivatorNodeRef?: ReturnType<typeof useSortable>['setActivatorNodeRef'];
  style: {
    transform: string | undefined;
    transition: string;
    opacity: number;
    zIndex: number;
    position: 'relative' | 'static';
    pointerEvents: 'none' | 'auto';
  };
  isDragging: boolean;
}

/**
 * Custom hook for handling task drag-and-drop functionality
 * Provides a consistent interface for making task items draggable
 */
export const useTaskDrag = ({ id, task, containerId }: UseTaskDragOptions): UseTaskDragResult => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id,
    data: {
      type: 'task',
      task,
      sortable: {
        containerId: containerId || (task.isCompleted ? 'completed-tasks' : 'active-tasks')
      }
    }
  });

  return {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    style: {
      transform: CSS.Transform.toString(transform),
      transition: transition || 'transform 200ms cubic-bezier(0.2, 0, 0, 1)',
      opacity: isDragging ? 0.4 : 1, // Less transparent to be more visible
      zIndex: isDragging ? 999 : 1,
      position: isDragging ? 'relative' : 'static',
      pointerEvents: isDragging ? 'none' : 'auto',
    },
    isDragging
  };
};

export default useTaskDrag;
